import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isGuest } from '../lib/guestMode';
import GuestEmptyState from '../components/GuestEmptyState';
import { useProfile } from '../context/ProfileContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { locationService } from '../lib/locationService';
import { Capacitor } from '@capacitor/core';
import TutorialOverlay from '../components/TutorialOverlay';
import AudioVideoSettings from '../components/AudioVideoSettings';
import { LifeBuoy } from 'lucide-react';

// Conditionally import OneSignal (only available on native)
let OneSignal = null;
if (Capacitor.isNativePlatform()) {
  OneSignal = require('onesignal-cordova-plugin').default;
}

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const CheckCircleIcon = ({ color }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const XCircleIcon = ({ color }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const InfoCircleIcon = ({ color }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const SwitchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 3h5v5" />
    <path d="M8 21H3v-5" />
    <path d="M21 3l-9 9" />
    <path d="M3 21l9-9" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const Profile = () => {
  const { user, session, signOut } = useAuth();
  const { isViewerOnly, linkedParents, activeProfileId, userProfile, activeProfile, canTrade } = useProfile();
  const { isDark, toggleTheme, theme } = useTheme();

  // Child has parent if they have linkedParents OR a parent is viewing as them
  const hasParent = (linkedParents && linkedParents.length > 0) ||
                    (activeProfileId && userProfile?.is_parent);

  // Parent is viewing as a child profile
  const isViewingAsChild = activeProfileId && userProfile?.is_parent && activeProfileId !== userProfile?.id;

  // Which profile to display in the Profile section
  // If viewing as child, show child's info; otherwise show logged-in user's info
  const displayProfile = isViewingAsChild ? activeProfile : null; // will fallback to `profile` state

  const { resetAllTutorials, showProfileFarewell, handleFarewellComplete, currentStep, hideMyPosts, setHideMyPosts, setCurrentStep } = useTutorial();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resettingTutorial, setResettingTutorial] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Parent setup state (for viewer-only minors)
  const [showParentSetup, setShowParentSetup] = useState(false);
  const [parentEmail, setParentEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteMessageType, setInviteMessageType] = useState('success'); // 'success' or 'info'
  const [removingInvite, setRemovingInvite] = useState(null);


  // Family setup state (for adults to become parents)
  const [showFamilySetup, setShowFamilySetup] = useState(false);
  const [settingUpFamily, setSettingUpFamily] = useState(false);

  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [locationMethod, setLocationMethod] = useState('gps'); // 'gps' or 'manual'
  const [manualLocation, setManualLocation] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationSaved, setLocationSaved] = useState(false);
  const [searchRadius, setSearchRadius] = useState(25); // miles
  const [showLocationEdit, setShowLocationEdit] = useState(false);

  // Privacy settings
  const [hideProfile, setHideProfile] = useState(false);

  // Title screen preference (skip Game Boy intro)
  const [skipTitleScreen, setSkipTitleScreen] = useState(() => {
    return localStorage.getItem('skipTitleScreen') === 'true';
  });

  // Audio preferences
  const [musicEnabled, setMusicEnabled] = useState(() => {
    return localStorage.getItem('musicEnabled') !== 'false'; // default ON
  });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('soundEnabled') !== 'false'; // default ON
  });
  const [preferDigital, setPreferDigital] = useState(() => {
    return localStorage.getItem('imagePreference') !== 'photo'; // default Digital
  });

  // Audio/Video settings modal
  const [showAudioSettings, setShowAudioSettings] = useState(false);

  // Notification debug
  const [showNotificationDebug, setShowNotificationDebug] = useState(false);
  const [notificationDebugInfo, setNotificationDebugInfo] = useState(null);
  const [debugCopied, setDebugCopied] = useState(false);

  // Push notification settings
  const [pushPermission, setPushPermission] = useState(false);
  const [deviceCount, setDeviceCount] = useState(0);
  const [statusChecking, setStatusChecking] = useState(false);
  const [statusModal, setStatusModal] = useState({ show: false, status: null, message: '', devices: [] });
  const isNative = Capacitor.isNativePlatform();

  // Location staff/owner state
  const [isLocationStaff, setIsLocationStaff] = useState(false);
  const [locationStaffInfo, setLocationStaffInfo] = useState(null);
  const [pendingClaim, setPendingClaim] = useState(null); // { status, storeName, denialReason }

  // Sync hideProfile from correct profile when viewing as child
  useEffect(() => {
    if (isViewingAsChild && activeProfile) {
      // When viewing as child, use child's hide_profile setting
      setHideProfile(activeProfile.hide_profile || false);
    } else if (profile) {
      // When viewing as self, use own profile's setting
      setHideProfile(profile.hide_profile || false);
    }
  }, [isViewingAsChild, activeProfile?.id, activeProfile?.hide_profile, profile]);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }

        if (data) {
          setProfile(data);
          // Load existing location settings
          setLocationEnabled(data.location_enabled || false);
          setLocationMethod(data.location_method || 'gps');
          setManualLocation(data.manual_location || '');
          setSearchRadius(data.search_radius || 25);
          if (data.latitude && data.longitude) {
            setCurrentLocation({
              latitude: data.latitude,
              longitude: data.longitude,
              city: data.location_city,
              state: data.location_state,
            });
          }
          // Load privacy settings
          setHideProfile(data.hide_profile || false);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id]);

  // Fetch pending invites when profile loads (for minors)
  useEffect(() => {
    if (profile?.id && profile?.is_under_18) {
      fetchPendingInvites();
    }
  }, [profile?.id, profile?.is_under_18]);

  // Check if user is location staff/owner
  useEffect(() => {
    const checkLocationStaff = async () => {
      if (!activeProfile?.id) return;

      try {
        const { data, error } = await supabase
          .from('location_staff')
          .select(`
            role,
            status,
            locations (
              id,
              store_name
            )
          `)
          .eq('user_id', activeProfile.id)
          .in('status', ['active', 'pending'])
          .maybeSingle();

        if (!error && data?.locations) {
          setIsLocationStaff(true);
          setLocationStaffInfo({
            role: data.role,
            status: data.status,
            storeName: data.locations.store_name,
          });
        } else {
          setIsLocationStaff(false);
          setLocationStaffInfo(null);
        }
      } catch (err) {
        // No location staff record - that's fine
        setIsLocationStaff(false);
      }
    };

    checkLocationStaff();
  }, [activeProfile?.id]);

  // Check for pending location claims
  useEffect(() => {
    const checkPendingClaims = async () => {
      if (!activeProfile?.id || isLocationStaff) return;

      try {
        const { data, error } = await supabase
          .from('location_claims')
          .select(`
            status,
            denial_reason,
            location:locations(store_name)
          `)
          .eq('claimant_id', activeProfile.id)
          .in('status', ['pending', 'denied'])
          .order('submitted_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          setPendingClaim({
            status: data.status,
            storeName: data.location?.store_name || 'Unknown',
            denialReason: data.denial_reason,
          });
        } else {
          setPendingClaim(null);
        }
      } catch {
        setPendingClaim(null);
      }
    };

    checkPendingClaims();
  }, [activeProfile?.id, isLocationStaff]);

  // Check push notification permission on mount
  useEffect(() => {
    const checkPushPermission = async () => {
      if (!isNative || !OneSignal) return;

      try {
        const enabled = OneSignal.User?.pushSubscription?.optedIn;
        setPushPermission(enabled || false);

        // Get device count
        if (activeProfile?.id) {
          const { data: devices } = await supabase
            .from('user_devices')
            .select('id')
            .eq('user_id', activeProfile.id);
          setDeviceCount(devices?.length || 0);
        }
      } catch (err) {
        console.error('[Profile] Error checking push permission:', err);
      }
    };

    checkPushPermission();
  }, [activeProfile?.id, isNative]);

  // Handle GPS location request
  const handleGetGPSLocation = async () => {
    setLocationLoading(true);
    setLocationError(null);
    setLocationSaved(false);

    const result = await locationService.getCurrentLocation();

    if (result.success) {
      // Reverse geocode to get city/state
      const geoResult = await locationService.reverseGeocode(
        result.coords.latitude,
        result.coords.longitude
      );

      const locationData = {
        latitude: result.coords.latitude,
        longitude: result.coords.longitude,
        city: geoResult.success ? geoResult.city : null,
        state: geoResult.success ? geoResult.state : null,
      };

      setCurrentLocation(locationData);
      setLocationMethod('gps');

      // Save to database
      await saveLocationToDatabase(locationData, 'gps');
    } else {
      setLocationError(result.error);
    }

    setLocationLoading(false);
  };

  // Handle manual location search
  const handleManualLocationSearch = async () => {
    if (!manualLocation.trim()) {
      setLocationError('Please enter a city or zip code');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);
    setLocationSaved(false);

    const result = await locationService.geocodeAddress(manualLocation.trim());

    if (result.success) {
      // Get city/state from the result
      const geoResult = await locationService.reverseGeocode(
        result.coords.latitude,
        result.coords.longitude
      );

      const locationData = {
        latitude: result.coords.latitude,
        longitude: result.coords.longitude,
        city: geoResult.success ? geoResult.city : null,
        state: geoResult.success ? geoResult.state : null,
      };

      setCurrentLocation(locationData);
      setLocationMethod('manual');

      // Save to database
      await saveLocationToDatabase(locationData, 'manual');
    } else {
      setLocationError('Could not find that location. Try a different city or zip code.');
    }

    setLocationLoading(false);
  };

  // Save location to database
  const saveLocationToDatabase = async (locationData, method) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({
          location_enabled: true,
          location_method: method,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          location_city: locationData.city,
          location_state: locationData.state,
          manual_location: method === 'manual' ? manualLocation : null,
          search_radius: searchRadius,
          location_updated_at: new Date().toISOString(),
        })
        .eq('auth_id', user.id);

      if (error) throw error;

      setLocationEnabled(true);
      setLocationSaved(true);
      setShowLocationEdit(false);
      setTimeout(() => setLocationSaved(false), 3000);
    } catch (err) {
      console.error('Error saving location:', err);
      setLocationError('Failed to save location. Please try again.');
    }
  };

  // Toggle location off
  const handleDisableLocation = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({
          location_enabled: false,
          latitude: null,
          longitude: null,
          location_city: null,
          location_state: null,
        })
        .eq('auth_id', user.id);

      if (error) throw error;

      setLocationEnabled(false);
      setCurrentLocation(null);
      setLocationError(null);
    } catch (err) {
      console.error('Error disabling location:', err);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);

    try {
      // Get the user's DB id
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userData?.id) {
        const userId = userData.id;

        // Get child IDs linked to this parent
        const { data: childLinks } = await supabase
          .from('family_links')
          .select('child_id')
          .eq('parent_id', userId);

        const childIds = (childLinks || []).map(l => l.child_id);

        // All profile IDs to clean up (self + children without own auth)
        const allIds = [userId, ...childIds];

        // Delete in order to avoid FK issues
        // Messages, trade_schedules, trade_reviews, trade_requests, cards
        for (const id of allIds) {
          await supabase.from('messages').delete().eq('sender_id', id);
          await supabase.from('trade_reviews').delete().or(`reviewer_id.eq.${id},reviewed_id.eq.${id}`);
          await supabase.from('trade_schedules').delete().or(`requester_id.eq.${id},owner_id.eq.${id}`);
          await supabase.from('trade_requests').delete().or(`requester_id.eq.${id},card_owner_id.eq.${id}`);
          await supabase.from('cards').delete().eq('user_id', id);
          await supabase.from('user_follows').delete().or(`follower_id.eq.${id},following_id.eq.${id}`);
          await supabase.from('not_interested_cards').delete().eq('user_id', id);
          await supabase.from('tutorial_progress').delete().eq('user_id', id);
          await supabase.from('user_devices').delete().eq('user_id', id);
        }

        // Delete family links
        await supabase.from('family_links').delete().eq('parent_id', userId);
        await supabase.from('family_links').delete().in('child_id', [userId]);

        // Delete child user rows (ones without their own auth)
        for (const childId of childIds) {
          const { data: child } = await supabase
            .from('users')
            .select('has_own_auth')
            .eq('id', childId)
            .single();

          if (child && !child.has_own_auth) {
            await supabase.from('users').delete().eq('id', childId);
          }
        }

        // Delete the user row
        await supabase.from('users').delete().eq('id', userId);
      }

      // Sign out and navigate away
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('Something went wrong. Please try again.');
      setDeleting(false);
    }
  };

  // Toggle hide profile setting
  const handleToggleHideProfile = async () => {
    // Determine which profile to update
    const targetProfileId = isViewingAsChild ? activeProfile?.id : profile?.id;
    if (!targetProfileId) return;

    const newValue = !hideProfile;
    setHideProfile(newValue); // Optimistic update

    try {
      const { error } = await supabase
        .from('users')
        .update({ hide_profile: newValue })
        .eq('id', targetProfileId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating hide profile:', err);
      setHideProfile(!newValue); // Revert on error
    }
  };

  // Toggle skip title screen (Game Boy intro)
  const handleToggleSkipTitle = () => {
    const newValue = !skipTitleScreen;
    setSkipTitleScreen(newValue);
    localStorage.setItem('skipTitleScreen', newValue.toString());
    if (userProfile?.id) {
      supabase.from('users').update({ pref_skip_title_screen: newValue }).eq('id', userProfile.id).then(() => {});
    }
  };

  // Toggle music (PokemonCenter background music in-app)
  const handleToggleMusic = () => {
    const newValue = !musicEnabled;
    setMusicEnabled(newValue);
    localStorage.setItem('musicEnabled', newValue.toString());
  };

  // Toggle sound effects (button clicks, etc.)
  const handleToggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('soundEnabled', newValue.toString());
  };

  // Toggle image preference (digital vs photo)
  const handleToggleImagePreference = () => {
    const newValue = !preferDigital;
    setPreferDigital(newValue);
    localStorage.setItem('imagePreference', newValue ? 'digital' : 'photo');
  };

  // Notification debug - gather all relevant info for ALL profiles on account
  const handleNotificationDebug = async () => {
    setDebugCopied(false);
    const info = {
      timestamp: new Date().toISOString(),
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform(),
      activeProfileId: activeProfile?.id || 'not set',
      activeProfileName: activeProfile?.username || 'unknown',
      parentId: profile?.id || 'not loaded',
      authId: user?.id || 'not logged in',
    };

    // Get OneSignal info if on native
    if (Capacitor.isNativePlatform() && OneSignal) {
      try {
        info.hasPermission = await OneSignal.Notifications.getPermissionAsync();
        info.playerId = OneSignal.User.pushSubscription?.id || 'not available';
        info.optedIn = OneSignal.User.pushSubscription?.optedIn || false;
      } catch (e) {
        info.oneSignalError = e.message;
      }
    } else {
      info.oneSignalNote = 'Web platform - no push';
    }

    // Get ALL profiles on this account and their device registrations
    if (profile?.id) {
      // Get children linked to this parent
      const { data: childLinks } = await supabase
        .from('family_links')
        .select(`
          child_id,
          users!family_links_child_id_fkey (
            username
          )
        `)
        .eq('parent_id', profile.id);

      const allProfiles = [
        { id: profile.id, name: profile.username || 'Parent' }
      ];
      if (childLinks) {
        childLinks.forEach(link => {
          const childName = link.users?.username || 'Child';
          allProfiles.push({ id: link.child_id, name: childName });
        });
      }

      info.profilesOnAccount = allProfiles.length;
      info.devicesByProfile = {};

      // Get devices for each profile
      for (const p of allProfiles) {
        const { data: devices } = await supabase
          .from('user_devices')
          .select('onesignal_player_id, platform, last_active_at')
          .eq('user_id', p.id);

        info.devicesByProfile[p.name] = {
          userId: p.id,
          devices: devices || []
        };
      }
    }

    setNotificationDebugInfo(info);
    setShowNotificationDebug(true);
  };

  const copyDebugInfo = () => {
    const text = JSON.stringify(notificationDebugInfo, null, 2);
    // Use textarea fallback that works in webviews
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setDebugCopied(true);
    } catch (e) {
      console.error('Copy failed:', e);
    }
    document.body.removeChild(textarea);
  };

  const requestNotificationPermission = async () => {
    if (!Capacitor.isNativePlatform() || !OneSignal) {
      alert('Only available on native app');
      return;
    }
    try {
      const granted = await OneSignal.Notifications.requestPermission(true);
      alert(granted ? 'Permission granted!' : 'Permission denied');
      // Refresh debug info
      handleNotificationDebug();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Handle check notification status (user-facing)
  const handleCheckNotificationStatus = async () => {
    if (!isNative) {
      setStatusModal({
        show: true,
        status: 'info',
        message: 'Push notifications are only available in the mobile app.',
        devices: []
      });
      return;
    }

    setStatusChecking(true);
    setStatusModal({ show: true, status: 'loading', message: 'Checking notification status...', devices: [] });

    try {
      const enabled = OneSignal?.User?.pushSubscription?.optedIn;
      setPushPermission(enabled);

      if (enabled) {
        // Re-register device
        if (activeProfile?.id) {
          OneSignal.login(activeProfile.id);
        }

        // Get devices
        const { data: devices } = await supabase
          .from('user_devices')
          .select('onesignal_player_id, platform, last_active_at')
          .eq('user_id', activeProfile?.id)
          .order('last_active_at', { ascending: false });

        setDeviceCount(devices?.length || 0);
        setStatusModal({
          show: true,
          status: 'success',
          message: 'Push notifications are enabled!',
          devices: devices || []
        });
      } else {
        // Request permission
        setStatusModal({ show: true, status: 'loading', message: 'Requesting permission...', devices: [] });

        const granted = await OneSignal.Notifications.requestPermission(true);
        setPushPermission(granted);

        if (granted) {
          OneSignal.User.pushSubscription.optIn();

          // Re-register device
          if (activeProfile?.id) {
            OneSignal.login(activeProfile.id);
          }

          // Refresh devices
          await new Promise(resolve => setTimeout(resolve, 1000));
          const { data: devices } = await supabase
            .from('user_devices')
            .select('onesignal_player_id, platform, last_active_at')
            .eq('user_id', activeProfile?.id)
            .order('last_active_at', { ascending: false });

          setDeviceCount(devices?.length || 0);
          setStatusModal({
            show: true,
            status: 'success',
            message: 'Push notifications enabled successfully!',
            devices: devices || []
          });
        } else {
          setStatusModal({
            show: true,
            status: 'error',
            message: 'Please enable notifications in Settings > Notifications.',
            devices: []
          });
        }
      }
    } catch (err) {
      console.error('[Profile] Error checking notification status:', err);
      setStatusModal({
        show: true,
        status: 'error',
        message: 'Error checking notification status.',
        devices: []
      });
    } finally {
      setStatusChecking(false);
    }
  };

  // Handle enable push notifications button
  const handleEnablePush = async () => {
    if (!isNative || !OneSignal) return;

    try {
      const granted = await OneSignal.Notifications.requestPermission(true);
      setPushPermission(granted);

      if (granted) {
        OneSignal.User.pushSubscription.optIn();
        if (activeProfile?.id) {
          OneSignal.login(activeProfile.id);
        }
      }
    } catch (err) {
      console.error('[Profile] Error enabling push:', err);
    }
  };

  const handleResetIntake = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('users')
        .update({ intake_completed: false })
        .eq('auth_id', user.id);

      if (error) throw error;
      setShowInfoModal(false);
      navigate('/title');
    } catch (err) {
      console.error('Error resetting intake:', err);
      alert('Error resetting intake');
    }
  };

  const handleResetTutorial = async () => {
    if (!user || resettingTutorial) return;
    setResettingTutorial(true);
    try {
      await resetAllTutorials();
      setShowInfoModal(false);
      alert('Tutorial reset! You will see the welcome prompt on next visit to Home.');
    } catch (err) {
      console.error('Error resetting tutorial:', err);
      alert('Error resetting tutorial');
    } finally {
      setResettingTutorial(false);
    }
  };

  const handleOpenTutorials = () => {
    setShowInfoModal(false);
    setCurrentStep(TUTORIAL_STEPS.YES_CHASE_HELLO);
    navigate('/home');
  };

  const handleSendParentInvite = async () => {
    if (!parentEmail.trim() || sendingInvite) return;

    setSendingInvite(true);
    setInviteError(null);
    setInviteMessage('');

    console.log('[Profile] Sending parent invite:', {
      childId: profile?.id,
      parentEmail: parentEmail.trim(),
      profileIsUnder18: profile?.is_under_18,
    });

    try {
      const { data, error } = await supabase.functions.invoke('invite-parent', {
        body: {
          childId: profile.id,
          parentEmail: parentEmail.trim(),
        },
      });

      console.log('[Profile] Invite result:', { data, error });

      if (error) throw error;

      if (data?.success) {
        setParentEmail('');
        setShowParentSetup(false);

        if (data.existingParent) {
          // Parent has account - request sent
          setInviteMessage('Request sent! Your parent will see it in their profile.');
          setInviteMessageType('success');
        } else {
          // Parent doesn't have account - invite sent
          setInviteMessage('No account found. Invite sent! Come back once your parent creates an account.');
          setInviteMessageType('info');
        }

        // Always fetch pending invites to update the list
        await fetchPendingInvites();
      } else {
        throw new Error(data?.error || 'Failed to send invite');
      }
    } catch (err) {
      console.error('Error sending parent invite:', err);
      setInviteError(err.message || 'Failed to send invite. Please try again.');
    } finally {
      setSendingInvite(false);
    }
  };

  const fetchPendingInvites = async () => {
    if (!profile?.id) {
      console.log('[Profile] fetchPendingInvites - no profile.id');
      return;
    }

    console.log('[Profile] Fetching pending invites for:', profile.id);

    try {
      const { data, error } = await supabase.functions.invoke('invite-parent', {
        body: {
          action: 'list',
          childId: profile.id,
        },
      });

      console.log('[Profile] Pending invites result:', { data, error });

      if (error) throw error;
      setPendingInvites(data?.invites || []);
    } catch (err) {
      console.error('Error fetching pending invites:', err);
    }
  };

  const handleRemoveInvite = async (invite) => {
    if (removingInvite) return;

    setRemovingInvite(invite.id || invite.parent_email);

    try {
      const { data, error } = await supabase.functions.invoke('invite-parent', {
        body: {
          action: 'remove',
          childId: profile.id,
          parentEmail: invite.parent_email,
          linkId: invite.id,
        },
      });

      if (error) throw error;

      if (data?.success) {
        setInviteMessage(data.message);
        await fetchPendingInvites();
      } else {
        throw new Error(data?.error || 'Failed to remove invite');
      }
    } catch (err) {
      console.error('Error removing invite:', err);
      setInviteError(err.message);
    } finally {
      setRemovingInvite(null);
    }
  };

  const handleResendInvite = async () => {
    // Reset invite sent state to show form again
    setInviteSent(false);
    setShowParentSetup(true);
    // Pre-fill with existing email if available
    if (profile?.parent_email) {
      setParentEmail(profile.parent_email);
    }
  };

  // Handle adult user becoming a parent
  const handleBecomeParent = async () => {
    if (!profile?.id || settingUpFamily) return;

    setSettingUpFamily(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          is_parent: true,
        })
        .eq('id', profile.id);

      if (error) throw error;

      // Update local profile state
      setProfile(prev => ({
        ...prev,
        is_parent: true,
      }));

      setShowFamilySetup(false);

      // Navigate to parent dashboard to add kids
      navigate('/profile-picker');
    } catch (err) {
      console.error('Error setting up family:', err);
      alert('Failed to set up family account. Please try again.');
    } finally {
      setSettingUpFamily(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return dateStr;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>
            <BackIcon />
          </button>
          <h1 style={styles.headerTitle}>Profile</h1>
          <div style={styles.headerBorder} />
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  // Guest mode: no profile exists, show sign-in prompt
  if (!user && isGuest()) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme?.bg, minHeight: '100vh' }}>
        <GuestEmptyState
          title="Sign in to create your profile"
          body="Create an account to build your trader reputation, adjust preferences and notification settings, and manage family profiles."
          action="Create Account"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Tutorial Farewell Overlay */}
      <TutorialOverlay
        visible={showProfileFarewell}
        message={[
          "This is where all the tutorials",
          "will be if you ever need help!",
          "",
          "My partner Chase will help you",
          "from here. Good luck with all",
          "your trades!"
        ]}
        spotlightMode="info-icon"
        onYes={handleFarewellComplete}
        yesText="Done"
      />

      {/* Info Modal with Reset Buttons */}
      {showInfoModal && (
        <div style={styles.modalOverlay} onClick={() => setShowInfoModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Developer Options</h3>
              <button style={styles.modalClose} onClick={() => setShowInfoModal(false)}>
                <XIcon />
              </button>
            </div>
            <div style={styles.modalContent}>
              <button style={styles.tutorialsButton} onClick={handleOpenTutorials}>
                Tutorials
              </button>

              <div style={styles.devSection}>
                <p style={styles.modalDesc}>Developer options for testing:</p>

                <button style={styles.resetButton} onClick={handleResetIntake}>
                  Reset Intake
                </button>

                <button
                  style={{
                    ...styles.resetButton,
                    borderColor: 'var(--purple)',
                    color: 'var(--purple)',
                    opacity: resettingTutorial ? 0.5 : 1,
                  }}
                  onClick={handleResetTutorial}
                  disabled={resettingTutorial}
                >
                  {resettingTutorial ? 'Resetting...' : 'Reset Tutorial'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Profile</h1>
        <button style={styles.infoButton} data-tutorial="tutorials" onClick={() => setShowInfoModal(true)}>
          <InfoIcon />
        </button>
        <div style={styles.headerBorder} />
      </div>

      {/* Content */}
      <div style={styles.content}>
        {user && (
          <>
            {/* 1. Profile Section - First */}
            {(profile || displayProfile) && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  {isViewingAsChild ? 'Viewing As' : 'Profile'}
                </h3>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Username</span>
                  <span style={styles.valueHighlight}>
                    @{(displayProfile || profile)?.username || 'N/A'}
                  </span>
                </div>
                {/* Only show email when viewing as self (not as child) */}
                {!isViewingAsChild && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Email</span>
                    <span style={styles.value}>{user.email}</span>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.label}>First Name</span>
                  <span style={styles.value}>
                    {(displayProfile || profile)?.first_name || 'N/A'}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Last Name</span>
                  <span style={styles.value}>
                    {(displayProfile || profile)?.last_name || 'N/A'}
                  </span>
                </div>
                {/* Only show birthday when viewing as self */}
                {!isViewingAsChild && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Birthday</span>
                    <span style={styles.value}>{formatDate(profile?.birth_date)}</span>
                  </div>
                )}
                {profile?.is_minor === 'true' && !isViewingAsChild && (
                  <>
                    <div style={styles.infoRow}>
                      <span style={styles.label}>Parent/Guardian</span>
                      <span style={styles.value}>
                        {profile.parent_first_name} {profile.parent_last_name}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Switch Profile Button - shows when parent is viewing as child */}
            {isViewingAsChild && (
              <button
                style={styles.switchProfileButton}
                onClick={() => navigate('/profile-picker')}
              >
                <div style={styles.switchProfileLeft}>
                  <div style={styles.switchProfileIcon}>
                    <SwitchIcon />
                  </div>
                  <div style={styles.switchProfileText}>
                    <span style={styles.switchProfileTitle}>Switch Profile</span>
                    <span style={styles.switchProfileDesc}>Change to a different account</span>
                  </div>
                </div>
                <ChevronRightIcon />
              </button>
            )}

            {/* Parent/Trading Section - for all minors (important for trading status) */}
            {profile?.is_under_18 && (
              <div style={styles.parentSetupSection}>
                <div style={styles.parentSetupHeader}>
                  <div style={styles.parentSetupIconBox}>
                    <ShieldIcon />
                  </div>
                  <div style={styles.parentSetupHeaderText}>
                    <h3 style={styles.parentSetupTitle}>
                      {canTrade ? 'Trading Enabled' : hasParent ? 'Trading Disabled' : 'Unlock Trading'}
                    </h3>
                    <p style={styles.parentSetupSubtitle}>
                      {canTrade
                        ? 'You can send and receive trade requests'
                        : hasParent
                          ? 'Ask your parent to enable trading'
                          : 'Add a parent to enable trading features'}
                    </p>
                  </div>
                </div>

                {canTrade ? (
                  <div style={styles.familyStatusSection}>
                    <div style={styles.familyStatusHeader}>
                      <div style={styles.familyStatusBadge}>
                        <CheckIcon />
                        <span>Trading Approved</span>
                      </div>
                    </div>
                    {linkedParents && linkedParents.length > 0 && (
                      <div style={styles.linkedParentsList}>
                        {linkedParents.map((link, idx) => (
                          <div key={idx} style={styles.linkedParentRow}>
                            <span style={styles.linkedParentName}>
                              {link.users?.username || 'Parent'}
                            </span>
                            <span style={styles.linkedParentStatus}>Active</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : hasParent ? (
                  <div style={styles.familyStatusSection}>
                    <div style={styles.familyStatusHeader}>
                      <div style={{...styles.familyStatusBadge, backgroundColor: 'rgba(245, 158, 11, 0.15)', color: 'var(--warning)'}}>
                        <span>Trading Disabled</span>
                      </div>
                    </div>
                    {linkedParents && linkedParents.length > 0 && (
                      <div style={styles.linkedParentsList}>
                        {linkedParents.map((link, idx) => (
                          <div key={idx} style={styles.linkedParentRow}>
                            <span style={styles.linkedParentName}>
                              {link.users?.username || 'Parent'}
                            </span>
                            <span style={{...styles.linkedParentStatus, color: link.can_trade ? 'var(--success)' : 'var(--warning)'}}>
                              {link.can_trade ? 'Can Trade' : 'View Only'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p style={styles.hasParentText}>
                      Ask your parent to enable trading from their profile.
                    </p>
                  </div>
                ) : (
                  <>
                    {inviteMessage && (
                      <div style={inviteMessageType === 'info' ? styles.inviteInfoBox : styles.inviteMessageBox}>
                        {inviteMessageType === 'success' && <CheckIcon />}
                        <span style={inviteMessageType === 'info' ? styles.inviteInfoText : styles.inviteMessageText}>{inviteMessage}</span>
                      </div>
                    )}

                    {inviteError && (
                      <div style={styles.inviteErrorBox}>
                        <span style={styles.inviteErrorText}>{inviteError}</span>
                      </div>
                    )}

                    {pendingInvites.length > 0 && (
                      <div style={styles.pendingInvitesSection}>
                        <p style={styles.pendingInvitesLabel}>Pending Requests:</p>
                        {pendingInvites.map((invite, idx) => {
                          // Handle both formats: invite.parent?.email (parent_child_links) and invite.parent_email (email-only)
                          const displayEmail = invite.parent?.email || invite.parent_email;
                          const displayName = invite.parent?.username;
                          const hasAccount = invite.parent?.email ? true : false;
                          return (
                            <div key={invite.id || idx} style={styles.pendingInviteCard}>
                              <div style={styles.pendingInviteHeader}>
                                <div style={styles.pendingInviteAvatar}>
                                  <UserIcon />
                                </div>
                                <div style={styles.pendingInviteDetails}>
                                  <span style={styles.pendingInviteName}>
                                    {displayName || 'Unknown'}
                                  </span>
                                  <span style={styles.pendingInviteEmail}>{displayEmail}</span>
                                  <span style={styles.pendingInviteRole}>Parent</span>
                                </div>
                                <button
                                  style={styles.removeInviteButton}
                                  onClick={() => handleRemoveInvite(invite)}
                                  disabled={removingInvite === (invite.id || invite.parent_email)}
                                >
                                  {removingInvite === (invite.id || invite.parent_email) ? '...' : 'X'}
                                </button>
                              </div>
                              <div style={{
                                ...styles.pendingInviteStatusBar,
                                backgroundColor: hasAccount ? 'var(--purple-bg)' : 'var(--warning-bg)',
                                borderColor: hasAccount ? 'var(--purple)' : 'var(--warning)',
                              }}>
                                <span style={{
                                  ...styles.pendingInviteStatusText,
                                  color: hasAccount ? 'var(--purple)' : 'var(--warning)'
                                }}>
                                  {hasAccount ? 'Waiting for approval' : 'No account - invite sent'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {showParentSetup ? (
                      <div style={styles.parentSetupForm}>
                        <p style={styles.parentSetupDesc}>
                          Enter your parent's email to check if they have an account.
                        </p>
                        <div style={styles.parentEmailInputRow}>
                          <div style={styles.parentEmailIcon}>
                            <MailIcon />
                          </div>
                          <input
                            type="email"
                            placeholder="parent@email.com"
                            value={parentEmail}
                            onChange={(e) => setParentEmail(e.target.value)}
                            style={styles.parentEmailInput}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendParentInvite()}
                          />
                        </div>
                        <div style={styles.parentSetupActions}>
                          <button
                            style={styles.cancelButton}
                            onClick={() => {
                              setShowParentSetup(false);
                              setInviteError(null);
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            style={{
                              ...styles.sendInviteButton,
                              opacity: !parentEmail.trim() || sendingInvite ? 0.5 : 1,
                            }}
                            onClick={handleSendParentInvite}
                            disabled={!parentEmail.trim() || sendingInvite}
                          >
                            {sendingInvite ? 'Checking...' : 'Check'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        style={{
                          ...styles.addParentButton,
                          opacity: pendingInvites.length >= 3 ? 0.5 : 1,
                        }}
                        onClick={() => setShowParentSetup(true)}
                        disabled={pendingInvites.length >= 3}
                      >
                        <span>{pendingInvites.length >= 3 ? 'Max Requests Sent' : 'Check for Parent'}</span>
                        {pendingInvites.length < 3 && <ChevronRightIcon />}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ========== MENU ITEMS ========== */}

            {/* Preferences */}
            <button style={styles.menuButton} onClick={() => navigate('/preferences')}>
              <div style={styles.menuLeft}>
                <div style={{...styles.menuIcon, backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6'}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                </div>
                <div style={styles.menuText}>
                  <span style={styles.menuTitle}>Preferences</span>
                  <span style={styles.menuDesc}>Location, appearance, privacy</span>
                </div>
              </div>
              <ChevronRightIcon />
            </button>

            {/* Family - for adults (non-minors), hidden when viewing as child */}
            {profile && profile.is_minor !== 'true' && !isViewingAsChild && (
              <button
                style={styles.menuButton}
                onClick={() => profile.is_parent ? navigate('/profile-picker') : setShowFamilySetup(true)}
              >
                <div style={styles.menuLeft}>
                  <div style={{...styles.menuIcon, backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981'}}>
                    <UsersIcon />
                  </div>
                  <div style={styles.menuText}>
                    <span style={styles.menuTitle}>Parental Controls</span>
                    <span style={styles.menuDesc}>
                      {profile.is_parent
                        ? 'Switch profiles, manage trading'
                        : 'Set up parental controls'}
                    </span>
                  </div>
                </div>
                <ChevronRightIcon />
              </button>
            )}

            {/* Support: version, updates, how to reach us */}
            <button style={styles.menuButton} onClick={() => navigate('/support')}>
              <div style={styles.menuLeft}>
                <div style={{...styles.menuIcon, backgroundColor: 'rgba(168, 85, 247, 0.15)', color: '#a855f7'}}>
                  <LifeBuoy size={18} />
                </div>
                <div style={styles.menuText}>
                  <span style={styles.menuTitle}>Support</span>
                  <span style={styles.menuDesc}>App version, updates, contact us</span>
                </div>
              </div>
              <ChevronRightIcon />
            </button>

            {/* Family Setup Confirmation - shown when user clicks Family but isn't a parent yet */}
            {showFamilySetup && profile && !profile.is_parent && (
              <div style={{...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border, marginBottom: 12}}>
                <p style={{...styles.settingDesc, color: theme.textSecondary, marginBottom: 12}}>
                  You will be acting as a parent, guardian, or overseer of other accounts. Do you want to continue?
                </p>
                <div style={{display: 'flex', gap: 12}}>
                  <button
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.bgCard,
                      color: theme.text,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowFamilySetup(false)}
                  >
                    Cancel
                  </button>
                  <button
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: 'none',
                      backgroundColor: theme.accent,
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      opacity: settingUpFamily ? 0.5 : 1,
                    }}
                    onClick={handleBecomeParent}
                    disabled={settingUpFamily}
                  >
                    {settingUpFamily ? 'Setting up...' : 'Continue'}
                  </button>
                </div>
              </div>
            )}

            {/* Following */}
            <button style={styles.menuButton} onClick={() => navigate('/following')}>
              <div style={styles.menuLeft}>
                <div style={{...styles.menuIcon, backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6'}}>
                  <UsersIcon />
                </div>
                <div style={styles.menuText}>
                  <span style={styles.menuTitle}>Following</span>
                  <span style={styles.menuDesc}>Traders you follow</span>
                </div>
              </div>
              <ChevronRightIcon />
            </button>

            {/* CardChase Location - only for store owners/staff */}
            {isLocationStaff && (
              <button style={styles.menuButton} onClick={() => navigate('/location-management')}>
                <div style={styles.menuLeft}>
                  <div style={{...styles.menuIcon, backgroundColor: 'rgba(236, 72, 153, 0.15)', color: '#ec4899'}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
                      <path d="M2 7h20" />
                    </svg>
                  </div>
                  <div style={styles.menuText}>
                    <span style={styles.menuTitle}>CardChase Location</span>
                    <span style={styles.menuDesc}>
                      {locationStaffInfo?.storeName || 'Your store'}
                    </span>
                  </div>
                </div>
                <ChevronRightIcon />
              </button>
            )}

            {/* Location Claim Status */}
            {!isLocationStaff && pendingClaim && (
              <div style={{
                ...styles.menuButton,
                cursor: 'default',
                borderLeft: `3px solid ${pendingClaim.status === 'pending' ? '#f59e0b' : '#ef4444'}`,
              }}>
                <div style={styles.menuLeft}>
                  <div style={{
                    ...styles.menuIcon,
                    backgroundColor: pendingClaim.status === 'pending'
                      ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: pendingClaim.status === 'pending' ? '#f59e0b' : '#ef4444',
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <div style={styles.menuText}>
                    <span style={styles.menuTitle}>
                      {pendingClaim.status === 'pending' ? 'Claim Pending Review' : 'Claim Denied'}
                    </span>
                    <span style={styles.menuDesc}>
                      {pendingClaim.storeName}
                      {pendingClaim.status === 'denied' && pendingClaim.denialReason
                        ? ` - ${pendingClaim.denialReason}`
                        : ''}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            <button style={styles.menuButton} onClick={() => navigate('/notifications')}>
              <div style={styles.menuLeft}>
                <div style={{...styles.menuIcon, backgroundColor: 'rgba(251, 146, 60, 0.15)', color: '#fb923c'}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <div style={styles.menuText}>
                  <span style={styles.menuTitle}>Notification Settings</span>
                  <span style={styles.menuDesc}>Devices & push notifications</span>
                </div>
              </div>
              <ChevronRightIcon />
            </button>

            {/* Audio Settings */}
            <button style={styles.menuButton} onClick={() => setShowAudioSettings(true)}>
              <div style={styles.menuLeft}>
                <div style={{...styles.menuIcon, backgroundColor: 'rgba(236, 72, 153, 0.15)', color: '#ec4899'}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                </div>
                <div style={styles.menuText}>
                  <span style={styles.menuTitle}>Audio Settings</span>
                  <span style={styles.menuDesc}>Music and sound effects</span>
                </div>
              </div>
              <ChevronRightIcon />
            </button>

            {/* 7. Sign Out */}
            <button style={styles.signOutButton} onClick={handleSignOut}>
              Sign Out
            </button>

            {/* 8. Delete Account */}
            <button
              style={styles.deleteAccountButton}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Account
            </button>

            {/* Delete Account Confirmation Modal */}
            {showDeleteConfirm && (
              <>
                <div style={styles.modalOverlay} onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }} />
                <div style={styles.deleteModal}>
                  <h3 style={styles.deleteModalTitle}>Delete Your Account?</h3>
                  <p style={styles.deleteModalText}>
                    This will permanently delete your account, all your cards, trades, messages, and any child profiles you created. This cannot be undone.
                  </p>
                  <p style={styles.deleteModalText}>
                    Type <strong>DELETE</strong> to confirm:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                    placeholder="Type DELETE"
                    style={styles.deleteModalInput}
                    disabled={deleting}
                  />
                  <div style={styles.deleteModalButtons}>
                    <button
                      style={styles.deleteModalCancel}
                      onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                      disabled={deleting}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        ...styles.deleteModalConfirm,
                        opacity: deleteConfirmText === 'DELETE' && !deleting ? 1 : 0.4,
                      }}
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== 'DELETE' || deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete Forever'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Notification Debug Modal */}
            {showNotificationDebug && (
              <>
                <div style={styles.modalOverlay} onClick={() => setShowNotificationDebug(false)} />
                <div style={styles.debugModal}>
                  <div style={styles.debugModalHeader}>
                    <span style={styles.debugModalTitle}>Notification Debug</span>
                    <button style={styles.debugModalClose} onClick={() => setShowNotificationDebug(false)}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {debugCopied && (
                    <div style={styles.debugCopiedBanner}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      Copied to clipboard!
                    </div>
                  )}
                  <pre style={styles.debugModalContent}>
                    {JSON.stringify(notificationDebugInfo, null, 2)}
                  </pre>
                  {notificationDebugInfo?.hasPermission === false && (
                    <button
                      style={{...styles.debugModalCopy, backgroundColor: '#f59e0b', marginBottom: 0}}
                      onClick={requestNotificationPermission}
                    >
                      Request Notification Permission
                    </button>
                  )}
                  <button
                    style={{...styles.debugModalCopy, backgroundColor: debugCopied ? '#22c55e' : '#0ea5e9'}}
                    onClick={copyDebugInfo}
                  >
                    {debugCopied ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                </div>
              </>
            )}

            {/* Push Notification Status Modal */}
            {statusModal.show && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: '20px',
                }}
                onClick={() => statusModal.status !== 'loading' && setStatusModal({...statusModal, show: false})}
              >
                <div
                  style={{
                    backgroundColor: theme.bgSheet,
                    borderRadius: '16px',
                    padding: '24px',
                    maxWidth: '320px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  {/* Status Icon */}
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                    {statusModal.status === 'loading' && (
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderTop: `4px solid ${theme.accent}`,
                        borderRight: `4px solid ${theme.border}`,
                        borderBottom: `4px solid ${theme.border}`,
                        borderLeft: `4px solid ${theme.border}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                    )}
                    {statusModal.status === 'success' && <CheckCircleIcon color={theme.success} />}
                    {statusModal.status === 'error' && <XCircleIcon color={theme.error} />}
                    {statusModal.status === 'info' && <InfoCircleIcon color={theme.accent} />}
                  </div>

                  {/* Message */}
                  <p style={{ fontSize: '16px', color: theme.text, margin: '0 0 16px 0', lineHeight: '1.5' }}>
                    {statusModal.message}
                  </p>

                  {/* Device list if success */}
                  {statusModal.devices.length > 0 && (
                    <div style={{
                      backgroundColor: theme.bgCard,
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      textAlign: 'left',
                    }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                        Registered Devices:
                      </p>
                      {statusModal.devices.map((device, i) => (
                        <div key={i} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: i < statusModal.devices.length - 1 ? `1px solid ${theme.border}` : 'none',
                        }}>
                          <span style={{ fontSize: '14px', color: theme.text, fontWeight: '500' }}>
                            {device.platform === 'ios' ? 'iPhone/iPad' : device.platform === 'android' ? 'Android' : device.platform}
                          </span>
                          <span style={{ fontSize: '12px', color: theme.textSecondary, fontFamily: 'monospace' }}>
                            ...{device.onesignal_player_id?.slice(-8)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Close Button */}
                  {statusModal.status !== 'loading' && (
                    <button
                      style={{
                        backgroundColor: theme.accent,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        width: '100%',
                      }}
                      onClick={() => setStatusModal({...statusModal, show: false})}
                    >
                      {statusModal.status === 'success' ? 'Done' : 'OK'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Audio Settings Modal */}
            {showAudioSettings && (
              <AudioVideoSettings onClose={() => setShowAudioSettings(false)} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: '100%',
    backgroundColor: 'var(--bg)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'var(--bg)',
    flexShrink: 0,
    position: 'relative',
    gap: '12px',
  },
  backButton: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text)',
  },
  headerTitle: {
    color: 'var(--text)',
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
    flex: 1,
    textAlign: 'center',
  },
  infoButton: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text)',
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: '40px',
    right: '40px',
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
    borderRadius: '1px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 16px var(--nav-clearance) 16px',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    borderTop: '3px solid var(--accent)',
    borderRight: '3px solid var(--border)',
    borderBottom: '3px solid var(--border)',
    borderLeft: '3px solid var(--border)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  smallSpinner: {
    width: '16px',
    height: '16px',
    borderTop: '2px solid var(--accent)',
    borderRight: '2px solid var(--border)',
    borderBottom: '2px solid var(--border)',
    borderLeft: '2px solid var(--border)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  section: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--accent)',
  },
  sectionTitleWithIcon: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  changeButton: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: '1px solid var(--accent)',
    borderRadius: '6px',
    color: 'var(--accent)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  sectionTitle: {
    color: 'var(--text-muted)',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 12px 0',
  },
  sectionHeader: {
    color: 'var(--text-muted)',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: '24px 0 12px 0',
    padding: 0,
  },
  locationDisplay: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  locationCity: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
  },
  locationMethod: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  noLocation: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  locationEditSection: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid var(--border)',
  },
  privacyNotice: {
    display: 'flex',
    gap: '10px',
    padding: '12px',
    backgroundColor: 'var(--accent-bg)',
    borderRadius: '8px',
    marginBottom: '16px',
    alignItems: 'flex-start',
    color: 'var(--accent)',
  },
  privacyText: {
    color: 'var(--text-muted)',
    fontSize: '12px',
    lineHeight: '1.5',
    margin: 0,
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '20px',
    backgroundColor: 'var(--bg-card)',
    borderRadius: '10px',
  },
  loadingText: {
    color: 'var(--text-muted)',
    fontSize: '14px',
  },
  methodSection: {
    marginTop: '8px',
  },
  optionCard: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    marginBottom: '10px',
    textAlign: 'left',
    cursor: 'pointer',
  },
  optionCardSelected: {
    borderColor: 'var(--accent)',
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  optionRadio: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid var(--text-disabled)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionRadioActive: {
    borderColor: 'var(--accent)',
  },
  optionRadioDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent)',
  },
  optionTitle: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
  },
  optionDesc: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
    marginLeft: '28px',
    display: 'block',
  },
  manualInputRow: {
    display: 'flex',
    gap: '10px',
    marginTop: '10px',
    marginLeft: '28px',
  },
  manualInput: {
    flex: 1,
    padding: '12px 14px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '14px',
    outline: 'none',
  },
  searchButton: {
    padding: '12px 20px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    minWidth: '70px',
  },
  gpsButton: {
    marginTop: '10px',
    marginLeft: '28px',
    padding: '12px 20px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  disableButton: {
    width: '100%',
    padding: '12px',
    marginTop: '6px',
    backgroundColor: 'transparent',
    border: '1px solid var(--text-disabled)',
    borderRadius: '8px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    cursor: 'pointer',
  },
  errorBox: {
    padding: '12px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    marginTop: '16px',
  },
  errorText: {
    color: 'var(--error)',
    fontSize: '13px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  label: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
  },
  value: {
    color: 'var(--text)',
    fontSize: '14px',
  },
  valueHighlight: {
    color: 'var(--accent)',
    fontSize: '14px',
    fontWeight: '600',
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  settingInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
  },
  settingLabel: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '500',
  },
  settingDesc: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  toggle: {
    width: '48px',
    height: '28px',
    borderRadius: '14px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.2s ease',
    flexShrink: 0,
  },
  toggleKnob: {
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    position: 'absolute',
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s ease',
  },
  themeToggle: {
    width: '48px',
    height: '28px',
    borderRadius: '14px',
    border: '1px solid',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
  themeToggleKnob: {
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    position: 'absolute',
    top: '1px',
    left: '1px',
    transition: 'transform 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
  },
  resetButton: {
    width: '100%',
    padding: '12px',
    marginBottom: '12px',
    backgroundColor: 'transparent',
    border: '1px solid var(--warning)',
    borderRadius: '8px',
    color: 'var(--warning)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  signOutButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'transparent',
    border: '1px solid var(--error)',
    borderRadius: '12px',
    color: 'var(--error)',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteAccountButton: {
    width: '100%',
    padding: '14px',
    marginTop: '12px',
    backgroundColor: 'var(--error)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteModal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'var(--bg-card)',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '340px',
    zIndex: 10001,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  deleteModalTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--error)',
  },
  deleteModalText: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    color: 'var(--text-muted)',
    lineHeight: '1.5',
  },
  deleteModalInput: {
    width: '100%',
    padding: '12px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
    textAlign: 'center',
    letterSpacing: '2px',
    boxSizing: 'border-box',
    outline: 'none',
    marginBottom: '16px',
  },
  deleteModalButtons: {
    display: 'flex',
    gap: '10px',
  },
  deleteModalCancel: {
    flex: 1,
    padding: '12px',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteModalConfirm: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    backgroundColor: 'var(--error)',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  // Switch Profile button styles
  switchProfileButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px',
    marginBottom: '20px',
    backgroundColor: 'var(--bg-sheet)',
    border: '1px solid var(--purple)',
    borderRadius: '12px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05))',
  },
  switchProfileLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  switchProfileIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--purple)',
  },
  switchProfileText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
  switchProfileTitle: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
  },
  switchProfileDesc: {
    color: 'var(--text-muted)',
    fontSize: '12px',
  },
  // Menu button styles (new slim profile layout)
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 16px',
    marginBottom: '8px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  menuLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  menuIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  menuTitle: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
  },
  menuDesc: {
    color: 'var(--text-muted)',
    fontSize: '12px',
  },
  // Legacy manage following button styles (for backwards compat)
  manageFollowingButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px',
    marginBottom: '20px',
    backgroundColor: 'var(--bg-sheet)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  manageFollowingLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  manageFollowingIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent)',
  },
  manageFollowingText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
  manageFollowingTitle: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
  },
  manageFollowingDesc: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    width: '100%',
    maxWidth: '340px',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid var(--border)',
  },
  modalTitle: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  modalClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  modalContent: {
    padding: '16px',
  },
  modalDesc: {
    color: 'var(--text-secondary)',
    fontSize: '13px',
    marginTop: 0,
    marginBottom: '12px',
  },
  tutorialsButton: {
    width: '100%',
    padding: '14px',
    marginBottom: '20px',
    backgroundColor: 'var(--accent)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  devSection: {
    paddingTop: '16px',
    borderTop: '1px solid var(--border)',
  },
  // Parent setup section styles
  parentSetupSection: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    border: '1px solid var(--warning)',
    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.05))',
  },
  parentSetupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '16px',
  },
  parentSetupIconBox: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--warning)',
    flexShrink: 0,
  },
  parentSetupHeaderText: {
    flex: 1,
  },
  parentSetupTitle: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  parentSetupSubtitle: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    margin: '4px 0 0 0',
  },
  parentSetupForm: {
    paddingTop: '8px',
  },
  parentSetupDesc: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    lineHeight: 1.5,
    margin: '0 0 16px 0',
  },
  parentEmailInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  parentEmailIcon: {
    padding: '12px 14px',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  parentEmailInput: {
    flex: 1,
    padding: '14px 14px 14px 0',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--text)',
    fontSize: '14px',
    outline: 'none',
  },
  inviteError: {
    color: 'var(--error)',
    fontSize: '12px',
    margin: '8px 0 0 0',
  },
  parentSetupActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid var(--text-disabled)',
    borderRadius: '8px',
    color: 'var(--text-muted)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  sendInviteButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'var(--warning)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--bg-pure)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  addParentButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(245, 158, 11, 0.15)',
    border: '1px solid var(--warning)',
    borderRadius: '10px',
    color: 'var(--warning)',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  hasParentMessage: {
    padding: '14px 16px',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    borderRadius: '10px',
  },
  hasParentText: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    lineHeight: 1.5,
    margin: 0,
    textAlign: 'center',
  },
  familyStatusSection: {
    padding: '12px',
    backgroundColor: 'rgba(100, 100, 100, 0.1)',
    borderRadius: '10px',
  },
  familyStatusHeader: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  familyStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderRadius: '20px',
    color: 'var(--success)',
    fontSize: '13px',
    fontWeight: '600',
  },
  linkedParentsList: {
    marginBottom: '12px',
  },
  linkedParentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: 'rgba(100, 100, 100, 0.15)',
    borderRadius: '8px',
    marginBottom: '6px',
  },
  linkedParentName: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  linkedParentStatus: {
    color: 'var(--success)',
    fontSize: '12px',
    fontWeight: '500',
  },
  inviteSentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inviteSentBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid var(--success)',
    borderRadius: '10px',
    color: 'var(--success)',
  },
  inviteSentText: {
    fontSize: '13px',
    lineHeight: 1.4,
  },
  resendButton: {
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: '1px solid var(--text-secondary)',
    borderRadius: '8px',
    color: 'var(--text-muted)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  inviteMessageBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid var(--success)',
    borderRadius: '10px',
    marginBottom: '12px',
  },
  inviteMessageText: {
    color: 'var(--success)',
    fontSize: '13px',
    lineHeight: 1.4,
  },
  inviteInfoBox: {
    padding: '12px 14px',
    backgroundColor: 'rgba(245, 158, 11, 0.15)',
    border: '1px solid var(--warning)',
    borderRadius: '10px',
    marginBottom: '12px',
  },
  inviteInfoText: {
    color: 'var(--warning)',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  inviteErrorBox: {
    padding: '12px 14px',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid var(--error)',
    borderRadius: '10px',
    marginBottom: '12px',
  },
  inviteErrorText: {
    color: 'var(--error)',
    fontSize: '13px',
    lineHeight: 1.4,
  },
  pendingInvitesSection: {
    marginBottom: '12px',
  },
  pendingInvitesLabel: {
    color: 'var(--text-muted)',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '10px',
    margin: '0 0 10px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pendingInviteCard: {
    backgroundColor: 'rgba(100, 100, 100, 0.1)',
    borderRadius: '12px',
    marginBottom: '10px',
    overflow: 'hidden',
  },
  pendingInviteHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    gap: '12px',
  },
  pendingInviteAvatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--purple)',
    flexShrink: 0,
  },
  pendingInviteDetails: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  pendingInviteName: {
    color: 'var(--text)',
    fontSize: '15px',
    fontWeight: '600',
  },
  pendingInviteEmail: {
    color: 'var(--text-muted)',
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '2px',
  },
  pendingInviteRole: {
    color: 'var(--text-secondary)',
    fontSize: '11px',
    marginTop: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pendingInviteStatusBar: {
    padding: '8px 12px',
    borderTop: '1px solid',
    textAlign: 'center',
  },
  pendingInviteStatusText: {
    fontSize: '12px',
    fontWeight: '500',
  },
  removeInviteButton: {
    padding: '8px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--error)',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    minWidth: '32px',
  },
  // Incoming parent requests styles (for adults)
  incomingRequestsSection: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    border: '1px solid var(--purple)',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05))',
  },
  incomingRequestsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  incomingRequestsTitle: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  incomingRequestsDesc: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    margin: '0 0 12px 0',
    lineHeight: 1.4,
  },
  incomingRequestRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    backgroundColor: 'rgba(100, 100, 100, 0.15)',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  incomingRequestInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  incomingRequestName: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  incomingRequestUsername: {
    color: 'var(--text-muted)',
    fontSize: '12px',
  },
  incomingRequestActions: {
    display: 'flex',
    gap: '8px',
  },
  declineRequestButton: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    border: '1px solid var(--text-secondary)',
    borderRadius: '6px',
    color: 'var(--text-muted)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  acceptRequestButton: {
    padding: '8px 14px',
    backgroundColor: 'var(--success)',
    border: 'none',
    borderRadius: '6px',
    color: 'var(--bg-pure)',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  // Family section styles (for adults)
  familySection: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '12px',
    border: '1px solid var(--purple)',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05))',
  },
  familySectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '16px',
  },
  familyIconBox: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--purple)',
    flexShrink: 0,
  },
  familySectionHeaderText: {
    flex: 1,
  },
  familySectionTitle: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  familySectionSubtitle: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    margin: '4px 0 0 0',
  },
  manageFamilyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    border: '1px solid var(--purple)',
    borderRadius: '10px',
    color: 'var(--purple)',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  setupFamilyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    border: '1px solid var(--purple)',
    borderRadius: '10px',
    color: 'var(--purple)',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  familySetupForm: {
    paddingTop: '8px',
  },
  familySetupDesc: {
    color: 'var(--text-muted)',
    fontSize: '13px',
    lineHeight: 1.5,
    margin: '0 0 16px 0',
  },
  familySetupActions: {
    display: 'flex',
    gap: '10px',
  },
  familyCancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid var(--text-disabled)',
    borderRadius: '8px',
    color: 'var(--text-muted)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  familyConfirmButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'var(--purple)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  // Debug modal styles
  debugModal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 32px)',
    maxWidth: '400px',
    maxHeight: '80vh',
    backgroundColor: 'var(--bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    zIndex: 10001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  debugModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid var(--border)',
  },
  debugModalTitle: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
  },
  debugModalClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-muted)',
  },
  debugModalContent: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    margin: 0,
    fontSize: '11px',
    fontFamily: 'monospace',
    color: 'var(--text)',
    backgroundColor: 'var(--bg)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  debugModalCopy: {
    margin: '16px',
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  debugCopiedBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    color: '#22c55e',
    fontSize: '14px',
    fontWeight: '600',
    borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
  },
};

export default Profile;
